import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../App";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { MapPin, Calendar, Clock, Heart, Music, ArrowRight, Trash2, List, CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, isToday } from "date-fns";
import { es } from "date-fns/locale";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MyEvents() {
  const { user, token } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("date");
  const [viewMode, setViewMode] = useState("list"); // "list" or "calendar"
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchFavorites();
  }, [sortBy]);

  const fetchFavorites = async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.get(`${API}/favorites`, {
        headers: authHeaders,
        params: { sort: sortBy }
      });
      setFavorites(res.data);
    } catch (error) {
      console.error("Error fetching favorites:", error);
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (eventId) => {
    try {
      await axios.delete(`${API}/favorites/${eventId}`, { headers: authHeaders });
      setFavorites(favorites.filter(f => f.event_id !== eventId));
      toast.success("ELIMINADO DE FAVORITOS");
    } catch (error) {
      toast.error("ERROR AL ELIMINAR");
    }
  };

  const formatEventDate = (date) => {
    try {
      return format(new Date(date), "EEEE d 'de' MMMM", { locale: es });
    } catch {
      return "";
    }
  };

  const formatEventTime = (date) => {
    try {
      return format(new Date(date), "HH:mm");
    } catch {
      return "";
    }
  };

  // Calendar helpers
  const calendarDays = useMemo(() => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const days = eachDayOfInterval({ start, end });
    
    // Add padding days from previous month
    const startDayOfWeek = start.getDay();
    const paddingStart = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1; // Monday = 0
    const paddingDays = [];
    for (let i = paddingStart; i > 0; i--) {
      const d = new Date(start);
      d.setDate(d.getDate() - i);
      paddingDays.push(d);
    }
    
    return [...paddingDays, ...days];
  }, [currentMonth]);

  const eventsByDate = useMemo(() => {
    const map = {};
    favorites.forEach(event => {
      const dateKey = format(new Date(event.event_date), "yyyy-MM-dd");
      if (!map[dateKey]) map[dateKey] = [];
      map[dateKey].push(event);
    });
    return map;
  }, [favorites]);

  const selectedDateEvents = useMemo(() => {
    if (!selectedDate) return [];
    const dateKey = format(selectedDate, "yyyy-MM-dd");
    return eventsByDate[dateKey] || [];
  }, [selectedDate, eventsByDate]);

  const goToPreviousMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const goToNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <Heart className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
          <h1 className="text-3xl font-black text-white mb-4">MIS EVENTOS</h1>
          <p className="text-zinc-500 font-mono mb-6">
            Inicia sesión para guardar tus eventos favoritos
          </p>
          <Link to="/login">
            <Button className="btn-ruido bg-primary text-white font-mono">
              INICIAR SESIÓN
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tighter">
              MIS <span className="text-primary">EVENTOS</span>
            </h1>
            <p className="font-mono text-sm text-zinc-500 mt-2">
              {favorites.length} evento{favorites.length !== 1 ? "s" : ""} guardado{favorites.length !== 1 ? "s" : ""}
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* View Mode Toggle */}
            <div className="flex bg-[#111] border border-zinc-800">
              <button
                onClick={() => setViewMode("list")}
                className={`px-4 py-2 font-mono text-sm flex items-center gap-2 transition-all ${
                  viewMode === "list" 
                    ? "bg-primary text-white" 
                    : "text-zinc-500 hover:text-white"
                }`}
                data-testid="view-list-btn"
              >
                <List className="w-4 h-4" />
                LISTA
              </button>
              <button
                onClick={() => setViewMode("calendar")}
                className={`px-4 py-2 font-mono text-sm flex items-center gap-2 transition-all ${
                  viewMode === "calendar" 
                    ? "bg-primary text-white" 
                    : "text-zinc-500 hover:text-white"
                }`}
                data-testid="view-calendar-btn"
              >
                <CalendarDays className="w-4 h-4" />
                CALENDARIO
              </button>
            </div>

            {viewMode === "list" && (
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger 
                  className="w-[200px] bg-[#111] border-zinc-800 font-mono text-sm rounded-none"
                  data-testid="sort-favorites"
                >
                  <SelectValue placeholder="Ordenar por" />
                </SelectTrigger>
                <SelectContent className="bg-[#111] border-zinc-800 rounded-none">
                  <SelectItem value="date" className="font-mono text-sm rounded-none">
                    POR FECHA DE EVENTO
                  </SelectItem>
                  <SelectItem value="added" className="font-mono text-sm rounded-none">
                    AÑADIDOS RECIENTEMENTE
                  </SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-[#111] border border-zinc-800 h-32 animate-pulse" />
            ))}
          </div>
        ) : favorites.length > 0 ? (
          viewMode === "list" ? (
            /* LIST VIEW */
            <div className="space-y-4">
              {favorites.map((event) => (
                <div
                  key={event.event_id}
                  className="group bg-[#111] border border-zinc-800 flex flex-col sm:flex-row overflow-hidden"
                  data-testid={`favorite-event-${event.event_id}`}
                >
                  {/* Date Column */}
                  <div className="bg-primary p-4 sm:p-6 flex sm:flex-col items-center justify-center sm:w-32 flex-shrink-0">
                    <span className="text-3xl sm:text-4xl font-black text-white">
                      {format(new Date(event.event_date), "d")}
                    </span>
                    <span className="text-sm font-mono text-white/80 ml-2 sm:ml-0 uppercase">
                      {format(new Date(event.event_date), "MMM", { locale: es })}
                    </span>
                    <span className="text-sm font-mono text-black ml-2 sm:ml-0 uppercase">
                      {format(new Date(event.event_date), "EEEE", { locale: es })}
                    </span>
                  </div>

                  {/* Image */}
                  {event.image_url && (
                    <div className="w-full sm:w-48 h-32 sm:h-auto flex-shrink-0 overflow-hidden">
                      <img
                        src={event.image_url}
                        alt={event.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}

                  {/* Content */}
                  <Link 
                    to={`/events/${event.event_id}`}
                    className="flex-1 p-4 sm:p-6 flex flex-col justify-center hover:bg-zinc-900/50 transition-colors"
                  >
                    <div className="flex flex-wrap items-center gap-4 mb-2 text-zinc-500 font-mono text-xs">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3 text-primary" />
                        {formatEventTime(event.event_date)}H
                        {event.end_date && ` - ${formatEventTime(event.end_date)}H`}
                      </span>
                      {event.bar_name && (
                        <>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {event.bar_name}
                          </span>
                        </>
                      )}
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-white group-hover:text-primary transition-colors mb-2">
                      {event.title}
                    </h3>
                    <p className="text-zinc-500 text-sm capitalize hidden sm:block">
                      {formatEventDate(event.event_date)}
                    </p>
                    
                    <div className="flex flex-wrap items-center gap-3 mt-3">
                      {event.price && (
                        <span className="bg-zinc-800 px-3 py-1 font-mono text-sm text-white">
                          {event.price}
                        </span>
                      )}
                      {event.tags?.map((tag) => (
                        <span key={tag} className="bg-primary/20 text-primary text-xs font-mono px-2 py-1 uppercase">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </Link>

                  {/* Actions */}
                  <div className="flex sm:flex-col items-center justify-end gap-2 p-4 border-t sm:border-t-0 sm:border-l border-zinc-800">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFavorite(event.event_id)}
                      className="text-zinc-500 hover:text-red-500 hover:bg-red-500/10"
                      data-testid={`remove-favorite-${event.event_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    <Link to={`/events/${event.event_id}`}>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-zinc-500 hover:text-primary"
                      >
                        <ArrowRight className="w-4 h-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* CALENDAR VIEW */
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Calendar Grid */}
              <div className="lg:col-span-2 bg-[#111] border border-zinc-800 p-6">
                {/* Calendar Header */}
                <div className="flex items-center justify-between mb-6">
                  <button 
                    onClick={goToPreviousMonth}
                    className="p-2 hover:bg-zinc-800 transition-colors"
                    data-testid="calendar-prev-month"
                  >
                    <ChevronLeft className="w-5 h-5 text-zinc-400" />
                  </button>
                  <h2 className="text-xl font-black text-white uppercase">
                    {format(currentMonth, "MMMM yyyy", { locale: es })}
                  </h2>
                  <button 
                    onClick={goToNextMonth}
                    className="p-2 hover:bg-zinc-800 transition-colors"
                    data-testid="calendar-next-month"
                  >
                    <ChevronRight className="w-5 h-5 text-zinc-400" />
                  </button>
                </div>

                {/* Day Headers */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {["LUN", "MAR", "MIÉ", "JUE", "VIE", "SÁB", "DOM"].map(day => (
                    <div key={day} className="text-center font-mono text-xs text-zinc-500 py-2">
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar Days */}
                <div className="grid grid-cols-7 gap-1">
                  {calendarDays.map((day, idx) => {
                    const dateKey = format(day, "yyyy-MM-dd");
                    const hasEvents = eventsByDate[dateKey]?.length > 0;
                    const eventCount = eventsByDate[dateKey]?.length || 0;
                    const isCurrentMonth = isSameMonth(day, currentMonth);
                    const isSelected = selectedDate && isSameDay(day, selectedDate);
                    const isDayToday = isToday(day);

                    return (
                      <button
                        key={idx}
                        onClick={() => setSelectedDate(day)}
                        className={`
                          aspect-square p-1 relative flex flex-col items-center justify-center
                          transition-all border
                          ${isCurrentMonth ? "text-white" : "text-zinc-600"}
                          ${isSelected ? "bg-primary border-primary" : "border-transparent hover:border-zinc-700"}
                          ${isDayToday && !isSelected ? "border-primary" : ""}
                        `}
                        data-testid={`calendar-day-${dateKey}`}
                      >
                        <span className={`text-sm font-mono ${isDayToday ? "font-bold" : ""}`}>
                          {format(day, "d")}
                        </span>
                        {hasEvents && (
                          <div className="flex gap-0.5 mt-0.5">
                            {eventCount <= 3 ? (
                              Array(eventCount).fill(0).map((_, i) => (
                                <span 
                                  key={i} 
                                  className={`w-1.5 h-1.5 rounded-full ${isSelected ? "bg-white" : "bg-primary"}`} 
                                />
                              ))
                            ) : (
                              <span className={`text-[10px] font-mono ${isSelected ? "text-white" : "text-primary"}`}>
                                {eventCount}
                              </span>
                            )}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Selected Day Events */}
              <div className="bg-[#111] border border-zinc-800 p-6">
                <h3 className="text-lg font-black text-white mb-4">
                  {selectedDate 
                    ? format(selectedDate, "EEEE d 'de' MMMM", { locale: es }).toUpperCase()
                    : "SELECCIONA UN DÍA"
                  }
                </h3>
                
                {selectedDate ? (
                  selectedDateEvents.length > 0 ? (
                    <div className="space-y-3">
                      {selectedDateEvents.map(event => (
                        <Link
                          key={event.event_id}
                          to={`/events/${event.event_id}`}
                          className="block bg-[#0a0a0a] border border-zinc-800 p-4 hover:border-primary transition-colors group"
                          data-testid={`calendar-event-${event.event_id}`}
                        >
                          <div className="flex items-center gap-2 text-zinc-500 font-mono text-xs mb-2">
                            <Clock className="w-3 h-3 text-primary" />
                            {formatEventTime(event.event_date)}H
                          </div>
                          <h4 className="font-bold text-white group-hover:text-primary transition-colors">
                            {event.title}
                          </h4>
                          {event.bar_name && (
                            <p className="text-zinc-500 text-sm mt-1 flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {event.bar_name}
                            </p>
                          )}
                          {event.price && (
                            <span className="inline-block mt-2 bg-zinc-800 px-2 py-0.5 font-mono text-xs text-white">
                              {event.price}
                            </span>
                          )}
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Calendar className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                      <p className="text-zinc-500 font-mono text-sm">
                        NO HAY EVENTOS ESTE DÍA
                      </p>
                    </div>
                  )
                ) : (
                  <div className="text-center py-8">
                    <CalendarDays className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                    <p className="text-zinc-500 font-mono text-sm">
                      HAZ CLIC EN UN DÍA PARA VER LOS EVENTOS
                    </p>
                  </div>
                )}
              </div>
            </div>
          )
        ) : (
          <div className="bg-[#111] border border-zinc-800 p-12 text-center">
            <Heart className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">NO TIENES EVENTOS GUARDADOS</h3>
            <p className="text-zinc-500 font-mono text-sm mb-6">
              Explora eventos y guárdalos para no perderte nada
            </p>
            <Link to="/events">
              <Button className="btn-ruido bg-primary text-white font-mono">
                EXPLORAR EVENTOS
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
